/**
 * Aylanuvchi yuklanish belgisi — FAQAT KICHIK, JOYIDA turadigan kutish uchun.
 *
 * ╔══════════════════════════════════════════════════════════════════════════╗
 * ║ SPINNER ENDI SAHIFA YUKLANISHINI KO'RSATMAYDI                           ║
 * ║                                                                          ║
 * ║ Ilgari bo'sh ekran o'rtasida aylanuvchi doira turardi. U foydalanuvchiga ║
 * ║ FAQAT «kutinglar» deydi va nima kelayotganini aytmaydi: sahifa ochilgach ║
 * ║ mazmun to'satdan paydo bo'lib, tugmalar joyidan siljib ketardi.          ║
 * ║                                                                          ║
 * ║ Endi sahifa darajasidagi kutish — `Skeleton` ning ishi: u kelayotgan     ║
 * ║ jadval/karta SHAKLINI oldindan chizadi, shuning uchun raqam kelganda     ║
 * ║ maket «sakramaydi».                                                      ║
 * ║                                                                          ║
 * ║ Spinner esa faqat shu ikki holatda qoladi:                               ║
 * ║   • tugma ichida — «bosildi, yuborilyapti» (`<Button loading>`);         ║
 * ║   • matn yonida — kichik, ikkilamchi so'rov (masalan valyuta kursi).     ║
 * ║                                                                          ║
 * ║ Shu sababli o'lcham `size-4` ga qotirilgan: u satr balandligiga mos      ║
 * ║ keladi va tugma ichida matnni surib yubormaydi. Kattaroq kerak bo'lsa —  ║
 * ║ bu Skeleton ishlatilishi kerak bo'lgan joy degan belgi.                  ║
 * ╚══════════════════════════════════════════════════════════════════════════╝
 */
import type { ReactNode } from 'react';
import { cn } from '../../lib/cn';
import { useT } from '../../lib/i18n-context';

export interface SpinnerProps {
  readonly className?: string;
  /** Skrinrider uchun matn. Berilmasa umumiy «Yuklanmoqda…». */
  readonly label?: string;
}

/**
 * Bezak halqasi — `role` ham, matn ham yo'q.
 *
 * Nima uchun alohida: tugma ichida spinner o'z nomini AYTMASLIGI kerak.
 * Tugmada allaqachon «Saqlash» yozuvi bor va `aria-busy` holatni bildiradi;
 * yonida yana «Yuklanmoqda…» eshitilsa, skrinrider bitta tugmani ikki marta
 * o'qigan bo'lardi. Bundan tashqari bu komponent hech qanday `hook`
 * chaqirmaydi — demak tarjima konteksti bo'lmagan joyda ham ishlaydi.
 *
 * ⚠️ Aylanish `motion-safe:` ostida: harakatni kamaytirish yoqilgan tizimda
 * doira qimirlamaydi. Holat shunda ham YO'QOLMAYDI — `<Button loading>`
 * o'zini bloklaydi va `disabled` ko'rinishiga o'tadi, skrinrider esa
 * `aria-busy` ni o'qiydi. Ya'ni kutish haqidagi xabar animatsiyaga
 * bog'lanib qolmagan.
 */
export function SpinnerGlyph({ className }: { readonly className?: string }): ReactNode {
  return (
    <span
      aria-hidden
      className={cn(
        'inline-block size-4 shrink-0 rounded-full border-2 border-current border-t-transparent',
        'align-[-2px] motion-safe:animate-spin',
        className,
      )}
    />
  );
}

export function Spinner({ className, label }: SpinnerProps): ReactNode {
  const t = useT();
  return (
    /*
     * `role="status"` — o'zgarish yumshoq (`polite`) e'lon qilinadi.
     * `alert` RAD ETILDI: yuklanish xato emas, uni skrinrider joriy jumlani
     * bo'lib o'qishga majburlash foydalanuvchini chalg'itardi.
     */
    <span role="status" aria-label={label ?? t('common.loading')} className={cn('inline-flex', className)}>
      <SpinnerGlyph />
    </span>
  );
}

/**
 * Butun ekranni egallaydigan yuklanish holati.
 *
 * ⚠️ FAQAT sessiya tiklanayotganda (`ProtectedRoute`) ishlatiladi: o'sha
 * paytda foydalanuvchi qaysi ekranga tushishi hali NOMA'LUM, demak skelet
 * chizishga shakl yo'q. Ma'lumot yuklanayotgan har qanday oddiy ekranda
 * bu emas, `Skeleton` ishlatiladi.
 */
export function FullPageSpinner({ label }: { readonly label?: string }): ReactNode {
  const t = useT();
  return (
    <div className="flex min-h-screen items-center justify-center gap-3 text-ink-muted">
      <Spinner label={label} />
      <span className="text-sm">{label ?? t('common.loading')}</span>
    </div>
  );
}
